import { useEffect, useState, useMemo } from "react";
import ReactSelect, { components } from "react-select";
import { toast } from "react-toastify";
import { useDebouncedCallback } from "use-debounce";
import apiClient from "../../../application/apis/api-client";
import {
  BaseProcedureEntity,
  ProcedureEntity,
} from "../../../application/domain/procedures/procedure-entity";
import { useAuthContextV2 } from "../../../auth-state";
import { reportUnexpectedError } from "../../../error-reporter";
import Analytics from "../../../../src/common/analytics";

export const ProcedureSelect: React.FC<{
  selected?: ProcedureEntity;
  onSelect(id?: string): void;
  tenantId?: string;
}> = ({ selected, onSelect, tenantId }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [procedures, setProcedures] = useState<BaseProcedureEntity[]>([]);
  const { auth } = useAuthContextV2();

  const searchProcedures = useDebouncedCallback(async (text = "") => {
    const limit = 50;

    try {
      const query: Parameters<typeof apiClient.fetchProcedures> = [
        1,
        limit,
        {
          name: text,
          // 申請が存在しうるステータスで絞り込む
          // 公開終了した手続きを受付前に戻せるため、BEFORE_ACCEPTING(受付前)の申請にも申請が存在する可能性がある
          publishStatus: [
            "BEFORE_ACCEPTING",
            "ACCEPTING",
            "PAUSING_ACCEPT",
            "FINISHED_ACCEPTING",
            "UNPUBLISHED",
          ],
        },
      ];
      const { data } = auth.isServiceProviderAccount()
        ? await apiClient.fetchProceduresByOrgId(tenantId!, ...query)
        : await apiClient.fetchProcedures(...query);
      setProcedures(data);
      // GAに手続き検索の結果を送信
      Analytics.procedureSearch(text, data.length);
    } catch (e) {
      reportUnexpectedError(e);
      toast.error("手続きの検索に失敗しました。");
    } finally {
      setIsLoading(false);
    }
  }, 1000);

  useEffect(() => {
    searchProcedures();
  }, [searchProcedures]);

  const candidates = useMemo(
    () =>
      [selected, ...procedures]
        .filter((proc): proc is BaseProcedureEntity => !!proc)
        .filter((proc, i, arr) => arr.findIndex((p) => p.id === proc.id) === i)
        .map((proc) => ({
          value: proc.id,
          label: proc.name,
        })),
    [selected, procedures],
  );

  return (
    <ReactSelect
      isClearable
      placeholder={
        candidates.length === 0 ? "手続き検索中..." : "手続きで絞り込む"
      }
      value={candidates.find((c) => c.value === selected?.id)}
      onInputChange={(text) => {
        setIsLoading(true);
        searchProcedures(text);
      }}
      onChange={(selected) => {
        onSelect(selected?.value);
      }}
      inputId={"procedure-select"}
      components={{
        Option: (p) => (
          <components.Option {...p}>
            <span data-for-rpa-id={`procedure-${p.label}`}>{p.children}</span>
          </components.Option>
        ),
      }}
      options={candidates}
      isLoading={isLoading}
      loadingMessage={() => "手続き検索中..."}
      noOptionsMessage={() => "手続きがありません"}
    />
  );
};
